import React from 'react';

import {
  dismissToast,
  isToastActive,
  showDefaultToast,
  showErrorToast,
  showInfoToast,
  showSuccessToast,
  showWarnToast,
  updateToast,
} from 'common/Toast';

import type { ToastData, ToastId, ToastOptions } from 'common/Toast';

const DEFAULT_OPTIONS = {
  autoClose: 3000
};

export interface Toast {
  //
  default: (message: ToastData, toastId: ToastId, options?: ToastOptions) => ToastId,
  success: (message: ToastData, toastId: ToastId, options?: ToastOptions) => ToastId,
  error: (message: ToastData, toastId: ToastId, options?: ToastOptions) => ToastId,
  warning: (message: ToastData, toastId: ToastId, options?: ToastOptions) => ToastId,
  info: (message: ToastData, toastId: ToastId, options?: ToastOptions) => ToastId,
  //
  dismiss: (id: ToastId) => void,
  update: (id: ToastId, options?: ToastOptions) => void,
  //
  isActive: (id: ToastId) => boolean
  //
}

export const useToast = (defaultOptions?: ToastOptions): Toast => {

  // Toast

  const toast = React.useCallback((message: ToastData, toastId: ToastId, options?: ToastOptions): ToastId => {
    return showDefaultToast(message, {
      toastId,
      ...DEFAULT_OPTIONS,
      ...defaultOptions,
      ...options,
    });
  }, [defaultOptions]);

  // Success

  const success = React.useCallback((message: ToastData, toastId: ToastId, options?: ToastOptions): ToastId => {
    return showSuccessToast(message, {
      toastId,
      ...DEFAULT_OPTIONS,
      ...defaultOptions,
      ...options
    });
  }, [defaultOptions]);

  // Error

  const error = React.useCallback((message: ToastData, toastId: ToastId, options?: ToastOptions): ToastId => {
    return showErrorToast(message, {
      toastId,
      ...DEFAULT_OPTIONS,
      ...defaultOptions,
      ...options
    });
  }, [defaultOptions]);

  // Warning

  const warning = React.useCallback((message: ToastData, toastId: ToastId, options?: ToastOptions): ToastId => {
    return showWarnToast(message, {
      toastId,
      ...DEFAULT_OPTIONS,
      ...defaultOptions,
      ...options
    });
  }, [defaultOptions]);

  // Info

  const info = React.useCallback((message: ToastData, toastId: ToastId, options?: ToastOptions): ToastId => {
    return showInfoToast(message, {
      toastId,
      ...DEFAULT_OPTIONS,
      ...defaultOptions,
      ...options
    });
  }, [defaultOptions]);

  // Dismiss Toast

  const dismiss = React.useCallback((id: ToastId) => {
    if (isToastActive(id)) {
      dismissToast(id);
    }
  }, []);

  // Update

  const update = React.useCallback((id: ToastId, options: ToastOptions = {}) => {
    return updateToast(id, {
      ...options,
    });
  }, []);

  // Is Active

  const isActive = React.useCallback((id: ToastId) => {
    return isToastActive(id);
  }, []);

  // Return

  return {
    //
    default: toast,
    success,
    error,
    warning,
    info,
    //
    dismiss,
    update,
    isActive,
    //
  };
};
